/**
 * Multi-tenant resolver with security validation
 * Resolves tenant from subdomain with strict validation
 */

import { config } from "./config";

const TENANT_REGEX = /^[a-z0-9-]{3,30}$/;
const DEFAULT_TENANT_UUID = "00000000-0000-0000-0000-000000000001";

export interface TenantConfig {
  id: string;
  name: string;
  logo?: string;
  theme: {
    primary: string;
    accent: string;
    radius: string;
  };
}

/**
 * Extract tenant ID from current hostname
 * Format: {tenantId}.domain.com
 * @deprecated Use getCurrentTenantId() instead for database queries
 */
export function resolveTenantId(): string {
  if (config.TENANT_RESOLVER === "path") {
    const pathMatch = window.location.pathname.match(/^\/([a-z0-9-]+)\//);
    if (pathMatch && TENANT_REGEX.test(pathMatch[1])) {
      return pathMatch[1];
    }
    return DEFAULT_TENANT_UUID;
  }

  // Subdomain resolver (default)
  const hostname = window.location.hostname;
  const parts = hostname.split(".");

  // localhost or IP - use default UUID
  if (parts.length < 2 || hostname === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return DEFAULT_TENANT_UUID;
  }

  const subdomain = parts[0];

  // Validate tenant ID format
  if (!TENANT_REGEX.test(subdomain)) {
    console.warn(`Invalid tenant ID format: ${subdomain}. Using default.`);
    return DEFAULT_TENANT_UUID;
  }

  return subdomain;
}

/**
 * Resolve tenant ID from database for authenticated user
 * This is the preferred method for database operations
 */
async function resolveTenantIdFromDatabase(): Promise<string> {
  // Dynamic import to avoid circular dependency
  const { supabase } = await import("@/integrations/supabase/client");
  
  try {
    const { data: session } = await supabase.auth.getSession();
    
    if (!session?.session?.user) {
      return DEFAULT_TENANT_UUID;
    }
    
    const { data: tenantUser, error } = await supabase
      .from('tenant_users')
      .select('tenant_id')
      .eq('user_id', session.session.user.id)
      .limit(1)
      .maybeSingle();
    
    if (error) {
      console.error('Error resolving tenant from database:', error);
      return DEFAULT_TENANT_UUID;
    }
    
    return tenantUser?.tenant_id || DEFAULT_TENANT_UUID;
  } catch (error) {
    console.error('Error in resolveTenantIdFromDatabase:', error);
    return DEFAULT_TENANT_UUID;
  }
}

/**
 * Load tenant configuration
 * In production, this would fetch from API
 */
export async function loadTenantConfig(tenantId: string): Promise<TenantConfig> {
  // For now, return mock config
  // In production: fetch from API with tenant isolation
  return {
    id: tenantId,
    name: tenantId === DEFAULT_TENANT_UUID ? "Pudinzim Sorocaba - Sistema de Gestão" : `${tenantId} Confeitaria`,
    theme: {
      primary: "38 92% 50%",
      accent: "25 85% 58%",
      radius: "0.75rem",
    },
  };
}

/**
 * Get current tenant ID from database (async)
 * This is the preferred method for all database operations
 * Returns UUID of the tenant the current user belongs to
 */
export async function getCurrentTenantId(): Promise<string> {
  try {
    const tenantId = await resolveTenantIdFromDatabase();
    
    // Validate UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(tenantId)) {
      console.error(`Invalid tenant UUID format: ${tenantId}. Using default.`);
      return DEFAULT_TENANT_UUID;
    }
    
    return tenantId;
  } catch (error) {
    console.error('Error getting current tenant ID:', error);
    return DEFAULT_TENANT_UUID;
  }
}

/**
 * Apply tenant theme to document
 */
export function applyTenantTheme(theme: TenantConfig["theme"]) {
  const root = document.documentElement;
  
  // Batch de setProperty (minimiza reflow) aplicado em um único frame
  requestAnimationFrame(() => {
    root.style.setProperty("--primary", theme.primary);
    root.style.setProperty("--accent", theme.accent);
    root.style.setProperty("--radius", theme.radius);
  });
}
